import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { buttonVariants } from "utils/muiTailwindStyles";
import PasswordRulesChecklist from "./PasswordRulesChecklist";

type UpdatePasswordFormType = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

interface Props {
  isSubmitting: boolean;
  isValid: boolean;
  isSuccess: boolean;
}

function UpdatePasswordForm({ isSubmitting, isValid, isSuccess }: Props) {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<UpdatePasswordFormType>();
  const [showCurrent, setShowCurrent] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const newPassword = watch("newPassword");
  const confirmNewPassword = watch("confirmNewPassword");

  return (
    <>
      <Box className="text-center space-y-6">
        <Typography
          variant="h5"
          component="h5"
          className="text-f22 leading-7 text-surface-dark"
        >
          Reset Password
        </Typography>
        <Typography variant="body2" className="text-black text-base px-4">
          Enter your current password and choose a new one.
        </Typography>
      </Box>

      <Box className="space-y-6">
        <Controller
          name="currentPassword"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              id="currentPassword"
              label="Current Password"
              variant="outlined"
              type={showCurrent ? "text" : "password"}
              className="w-full"
              error={!!errors.currentPassword}
              helperText={errors.currentPassword?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle current password visibility"
                        onClick={() => setShowCurrent((v) => !v)}
                        edge="end"
                      >
                        {showCurrent ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                htmlInput: { maxLength: 20, pattern: String.raw`[\x20-\x7E]*` },
              }}
            />
          )}
        />

        <Controller
          name="newPassword"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              id="newPassword"
              label="New Password"
              variant="outlined"
              type={showNew ? "text" : "password"}
              className="w-full"
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle new password visibility"
                        onClick={() => setShowNew((v) => !v)}
                        edge="end"
                      >
                        {showNew ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                htmlInput: { maxLength: 20, pattern: String.raw`[\x20-\x7E]*` },
              }}
            />
          )}
        />

        <PasswordRulesChecklist password={newPassword || ""} />

        <Controller
          name="confirmNewPassword"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              id="confirmNewPassword"
              label="Confirm New Password"
              variant="outlined"
              type={showConfirm ? "text" : "password"}
              className="w-full"
              error={!!errors.confirmNewPassword}
              helperText={errors.confirmNewPassword?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirm((v) => !v)}
                        edge="end"
                      >
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                htmlInput: { maxLength: 20, pattern: String.raw`[\x20-\x7E]*` },
              }}
            />
          )}
        />

        <Typography
          className={
            newPassword &&
            confirmNewPassword &&
            newPassword === confirmNewPassword
              ? "text-green-600 text-sm"
              : "text-gray-600 text-sm"
          }
        >
          The passwords must match
        </Typography>

        <Button
          type="submit"
          variant="contained"
          className={`${buttonVariants.primary.className} w-full text-base rounded-full py-3 disabled:text-surface-light disabled:opacity-40`}
          sx={{ ...buttonVariants.primary.sx }}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : isSuccess ? (
            "Updated"
          ) : (
            "Update"
          )}
        </Button>
      </Box>
    </>
  );
}

export default UpdatePasswordForm;
